import styles from './ProductModal.scss';

import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { ProductModule } from 'src/api/contentful/contentTypes.cloverdotcom';
import { Button } from 'src/components/Button/Button';
import { useFeatureFlagsContext } from 'src/components/FeatureFlags/FeatureFlags';
import { Grid } from 'src/components/Grid/Grid';
import { Icon, IconType } from 'src/components/Icon/Icon';
import { Modal } from 'src/components/Modal/Modal';
import { TextV2 } from 'src/components/TextV2/TextV2';
import { useDesktopOrLarge } from 'src/hooks/UseResponsive';
import { useDCS } from 'src/hooks/useDCS';
import { FlatEntry } from 'src/services/cms/flattenContentful';
import { ProductSlidesDesktop } from './ProductSlides/ProductSlidesDesktop/ProductSlidesDesktop';
import { ProductSlidesMobile } from './ProductSlides/ProductSlidesMobile/ProductSlidesMobile';

interface ProductModalProps {
  closeModal?: () => void;
  modalOpen?: boolean;
  productDetails?: FlatEntry<ProductModule>;
}

export const ProductModal: FC<ProductModalProps> = ({ closeModal, modalOpen = false, productDetails }) => {
  const { t } = useTranslation();

  const { description, name, productFeatures, showPrice, backgroundColor, dcsId } = productDetails || {};

  const { productsPricings } = useDCS({ productIds: dcsId ? [dcsId] : [] });

  const { isFlagEnabled } = useFeatureFlagsContext();
  const isMsrpEnabled = isFlagEnabled('show-msrp-global');
  const isDescriptionEnabled = isFlagEnabled('show-product-description-global');

  if (!productDetails) return null;

  const [productPricings] = productsPricings;

  const isDesktopOrLarger = useDesktopOrLarge();
  const ProductSlides = isDesktopOrLarger ? ProductSlidesDesktop : ProductSlidesMobile;

  const modalFeatures = () =>
    productFeatures?.map((item, index) => (
      <div key={index} className={styles.modalDetailsRow} tabIndex={0}>
        <Grid xs={12} md={12} lg={3}>
          <TextV2 tagName="span" value={item.title} className={styles.modalDetailsRowKey} variant="body-bold" />
        </Grid>
        <Grid xs={12} md={12} lg={9}>
          <TextV2 tagName="small" value={item.value} className={styles.modalDetailsRowValue} variant="body" isMarkdown />
        </Grid>
      </div>
    ));

  return (
    <Modal
      closeTimeoutMS={500}
      isOpen={modalOpen}
      onRequestClose={closeModal}
      className={styles.modalContainer}
      aria={{ modal: true, labelledby: 'productModal' }}
      overlayClassName={{ base: styles.productModalOverlay, afterOpen: styles.modalOpen, beforeClose: styles.modalClose }}
      focusElementId="modal-close"
      shouldCloseOnOverlayClick
      shouldCloseOnEsc
      shouldFocusAfterRender
    >
      <Button id="modal-close" onClick={closeModal} className={styles.closeIcon} aria-label={t('close')}><Icon type={IconType.CLOSE} /></Button>
        <Grid container justify-content="center" className={styles.modalDescriptionContainer} style={{ backgroundColor }}>
          <Grid xs={12} md={12} lg={7}>
            <div>
              <ProductSlides product={productDetails} />
            </div>
          </Grid>
          <Grid xs={12} md={12} lg={5} className={styles.modalDescriptionContainerText} id="fullDescription" tabIndex={0}>
              <TextV2 tagName="h4" value={name} variant="h4-bold" className={styles.modalDescriptionContainerTitle} id="productModal" />
              {showPrice && productPricings && (
                <>
                 {isMsrpEnabled && productPricings.msrp && <TextV2 tagName="span" value={t('pricing-detail-table.msrp', { price: productPricings.msrp.value })} className={styles.modalDescriptionContainerMsrp} variant="body-small" />}
                  <TextV2 tagName="span" value={t('pricing-detail-table.full-price', { basePrice: productPricings.basePrice.value })} className={`${styles.modalDescriptionContainerPrice} ${!isMsrpEnabled && styles.modalDescriptionContainerMsrpHide}`} variant="h3-medium" />
                  <div className={styles.modalDescriptionContainerPriceInfo}>
                    {productPricings.installmentPlans.IP3 && <TextV2 tagName="span" value={t('pricing-detail-table.secondary-price-info', { monthlyInstallmentPrice: productPricings.installmentPlans.IP3.value })} variant="body-medium" />}
                  </div>
                </>
              )}
              {isDescriptionEnabled && <TextV2 value={description} className={styles.modalDescriptionContainerValue} variant="body" isMarkdown tagName="small" />}
          </Grid>
        </Grid>
        <Grid container justify-content="center">{modalFeatures()}</Grid>
    </Modal>
  );
};

ProductModal.displayName = 'ProductModal';
